import React from 'react';
import { FaMoneyBillWave, FaPlus, FaClock } from 'react-icons/fa';
import { categoryLabels, billingCycleLabels } from './constants';

export function DuesOverview({ 
  duesConfig, 
  summary, 
  allocations, 
  canManageDues, 
  canChargeDues, 
  canAllocateFunds,
  accounts,
  onEditConfig,
  onAddAccount,
  onChargeAll,
  onAllocate
}) {
  return (
    <div className="space-y-6">
      {/* Family Dues Summary */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl shadow-md p-6 text-white">
        <h3 className="text-lg font-semibold mb-4">Family Dues Fund</h3>
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <p className="text-emerald-100 text-sm">Total Collected</p>
            <p className="text-3xl font-bold">${summary?.total_collected?.toFixed(2) || '0.00'}</p>
          </div>
          <div>
            <p className="text-emerald-100 text-sm">Outstanding Dues</p>
            <p className="text-2xl font-bold text-yellow-300">${Math.max(0, summary?.total_outstanding || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-emerald-100 text-sm">Allocated to Events</p>
            <p className="text-2xl font-bold">${summary?.total_allocated?.toFixed(2) || '0.00'}</p>
          </div>
          <div>
            <p className="text-emerald-100 text-sm">Available Balance</p>
            <p className="text-3xl font-bold text-green-300">${summary?.available_balance?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      </div>

      {/* Dues Configuration Display */}
      {duesConfig ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-800">Current Dues Structure</h3>
            {canManageDues && (
              <button
                onClick={onEditConfig}
                className="px-4 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 text-sm font-medium"
                data-testid="edit-dues-config-btn"
              >
                Edit Configuration
              </button>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-600">Billing Cycle: <span className="font-semibold text-gray-800">{billingCycleLabels[duesConfig.billing_cycle]}</span></p>
              <p className="text-gray-600">Due Day: <span className="font-semibold text-gray-800">{duesConfig.due_day}</span></p>
              {duesConfig.next_charge_date && (
                <p className="text-gray-600 mt-2">
                  Next Auto-Charge: <span className="font-semibold text-blue-600">
                    {new Date(duesConfig.next_charge_date).toLocaleDateString()}
                  </span>
                </p>
              )}
            </div>
            <div className="space-y-1">
              <p className="font-medium text-gray-700">Rates by Category:</p>
              {duesConfig.category_amounts?.map(ca => (
                <p key={ca.category} className="text-gray-600">
                  {categoryLabels[ca.category]}: <span className="font-semibold text-emerald-600">${ca.amount.toFixed(2)}</span>
                </p>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <FaMoneyBillWave className="text-gray-300 text-5xl mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700">No Dues Configuration</h3>
          <p className="text-gray-500 mb-4">Family dues have not been set up yet.</p>
          {canManageDues ? (
            <button onClick={onEditConfig} className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid="setup-dues-btn">
              Set Up Dues
            </button>
          ) : (
            <p className="text-sm text-gray-400">Contact a family administrator to configure dues.</p>
          )}
        </div>
      )}

      {/* Allocations */}
      {allocations.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Fund Allocations</h3>
          <div className="space-y-3">
            {allocations.map(alloc => (
              <div key={alloc.allocation_id} className="flex justify-between items-center border-b pb-3">
                <div>
                  <p className="font-medium text-gray-800">{alloc.event_name}</p>
                  <p className="text-sm text-gray-500">{new Date(alloc.created_at).toLocaleDateString()}</p>
                  {alloc.notes && <p className="text-sm text-gray-600">{alloc.notes}</p>}
                </div>
                <p className="text-lg font-bold text-purple-600">${alloc.amount.toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Actions */}
      {canManageDues && duesConfig && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Admin Actions</h3>
          <div className="flex flex-wrap gap-3">
            <button onClick={onAddAccount} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center space-x-2" data-testid="add-account-btn-overview">
              <FaPlus /> <span>Add Member Account</span>
            </button>
            {canChargeDues && (
              <button onClick={onChargeAll} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                <FaClock /> <span>Charge All Members</span>
              </button>
            )}
            {canAllocateFunds && (
              <button onClick={onAllocate} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                Allocate to Event
              </button>
            )}
          </div>
          {accounts.length === 0 && (
            <p className="mt-3 text-sm text-amber-600 bg-amber-50 p-2 rounded">
              Warning: No member accounts yet. Click "Add Member Account" first before charging dues.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
